import React, { useCallback, useEffect, useMemo, useState } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw,
  RotateCw,
  Mail,
  MessageSquare,
  BellRing,
  Filter,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  PlayCircle,
  Eye,
} from "lucide-react";

/**
 * Phase C1.4 — Super Admin → Replay Mocked Notifications.
 *
 * The Resend / Twilio / Web-push adapters write a `mock_send` row to
 * notifications_log every time live credentials were missing. After the
 * vault is populated, this tool lets the operator pick those rows and
 * resend them through the now-live channel — important for production
 * cutover (any host invitation / verification email that was mocked
 * pre-live should be replayed, not "we just hope they request again").
 *
 * Wire:
 *   GET  /api/super-admin/notifications/mocked        — list
 *   POST /api/super-admin/notifications/replay        — body {ids[], dry_run}
 */

const CHANNEL_META = {
  email: { label: "Email",  Icon: Mail },
  sms:   { label: "SMS",    Icon: MessageSquare },
  push:  { label: "Push",   Icon: BellRing },
};

export default function SuperAdminNotificationsReplay() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState("");
  const [filterOrg, setFilterOrg] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [working, setWorking] = useState(false);
  const [outcomes, setOutcomes] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    setOutcomes(null);
    setDryRunResult(null);
    try {
      const params = { limit: 200 };
      if (filterChannel) params.channel = filterChannel;
      if (filterOrg.trim()) params.org_id = filterOrg.trim();
      const { data } = await api.get("/super-admin/notifications/mocked", { params });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filterChannel, filterOrg]);

  useEffect(() => { refresh(); }, [refresh]);

  const channelsInSelection = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (selected.has(it.id)) set.add(it.channel); });
    return Array.from(set);
  }, [selected, items]);

  if (!authLoading && user?.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((it) => it.id)));
  };

  const replay = async (dry_run) => {
    if (selected.size === 0) {
      toast.error("Pick at least one mocked notification to replay.");
      return;
    }
    setWorking(true);
    setOutcomes(null);
    setDryRunResult(null);
    try {
      const { data } = await api.post("/super-admin/notifications/replay", {
        ids: Array.from(selected),
        dry_run: !!dry_run,
      });
      if (dry_run) {
        setDryRunResult(data);
        toast.message(`Dry run: would replay ${data.would_replay} rows across ${Object.keys(data.channels || {}).length} channel(s).`);
      } else {
        setOutcomes(data);
        const okCount = (data.outcomes || []).filter((o) => o.ok).length;
        const failCount = (data.outcomes || []).length - okCount;
        if (failCount === 0) {
          toast.success(`Replayed ${okCount} notification(s) successfully.`);
        } else {
          toast.warning(`Replayed ${okCount} OK · ${failCount} failed — see results below.`);
        }
        await refresh();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setWorking(false); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="notifications-replay-page">
        <div>
          <div className="eb-overline mb-1">Phase C1.4 · Notifications</div>
          <h1 className="text-3xl font-bold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <RotateCw className="w-7 h-7 text-orange-500" /> Replay mocked notifications
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-2 max-w-3xl">
            When Resend / Twilio / Web-push credentials were missing, the adapters
            wrote a <code className="px-1 rounded bg-navy-50 dark:bg-navy-700">mock_send</code> row
            to <code className="px-1 rounded bg-navy-50 dark:bg-navy-700">notifications_log</code>.
            After flipping a channel to <strong>live</strong>, replay these rows so the
            original recipients actually receive the email / SMS / push that the system
            already "thought" it sent. Every replay is audit-logged.
          </p>
        </div>

        {/* Filters */}
        <div className="eb-card p-4 flex flex-wrap items-end gap-3"
             data-testid="notifications-replay-filters">
          <div className="flex-1 min-w-[200px]">
            <label className="eb-label inline-flex items-center gap-1">
              <Filter className="w-3.5 h-3.5" /> Channel
            </label>
            <select
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
              className="eb-input"
              data-testid="filter-channel"
            >
              <option value="">All channels</option>
              <option value="email">Email (Resend)</option>
              <option value="sms">SMS (Twilio)</option>
              <option value="push">Push (Web Push)</option>
            </select>
          </div>
          <div className="flex-1 min-w-[260px]">
            <label className="eb-label">Org id (optional)</label>
            <input
              value={filterOrg}
              onChange={(e) => setFilterOrg(e.target.value)}
              placeholder="leave blank for all orgs"
              className="eb-input font-mono"
              data-testid="filter-org-id"
            />
          </div>
          <Button onClick={refresh} variant="outline" disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Selection bar */}
        <div className="eb-card p-4 flex flex-wrap items-center justify-between gap-3"
             data-testid="notifications-replay-actions">
          <div className="text-sm text-navy-700 dark:text-navy-200">
            <strong data-testid="selected-count">{selected.size}</strong> selected · {items.length} mocked total
            {channelsInSelection.length > 0 && (
              <span className="ml-2 text-navy-500">
                ({channelsInSelection.join(" · ")})
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={toggleAll} variant="outline" size="sm" data-testid="toggle-all-btn">
              {selected.size === items.length && items.length > 0 ? "Clear" : "Select all"}
            </Button>
            <Button
              onClick={() => replay(true)}
              variant="outline"
              size="sm"
              disabled={working || selected.size === 0}
              data-testid="dry-run-btn"
            >
              <Eye className="w-4 h-4 mr-1" /> Dry run
            </Button>
            <Button
              onClick={() => replay(false)}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              disabled={working || selected.size === 0}
              data-testid="replay-btn"
            >
              <PlayCircle className="w-4 h-4 mr-1" /> Replay selected
            </Button>
          </div>
        </div>

        {/* Dry-run / outcomes panel */}
        {dryRunResult && (
          <div className="eb-card p-4 border-warning-200 bg-warning-50" data-testid="dry-run-result">
            <div className="font-semibold text-warning-800 inline-flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4" /> Dry run — no notifications were sent
            </div>
            <div className="text-sm text-warning-900">
              Would replay <strong>{dryRunResult.would_replay}</strong> rows.
              Breakdown:&nbsp;
              {Object.entries(dryRunResult.channels || {}).map(([ch, n]) => (
                <span key={ch} className="mr-3 font-mono">{ch}={n}</span>
              ))}
            </div>
          </div>
        )}

        {outcomes && (
          <div className="eb-card p-4" data-testid="replay-outcomes">
            <div className="font-semibold inline-flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-success-600" />
              Replay results · {outcomes.replayed} processed
            </div>
            <div className="grid gap-2">
              {(outcomes.outcomes || []).map((o) => (
                <div key={o.id}
                     className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs ${
                       o.ok ? "bg-success-50 border-success-200 text-success-700"
                            : "bg-danger-50 border-danger-200 text-danger-700"
                     }`}
                     data-testid={`outcome-row-${o.id}`}>
                  <span className="font-mono">{o.id}</span>
                  <span className="flex items-center gap-2">
                    {o.ok
                      ? <><CheckCircle2 className="w-3 h-3" /> {o.mode}</>
                      : <><XCircle className="w-3 h-3" /> {o.error || o.skipped_reason || "failed"}</>}
                    {o.new_provider_id && <code className="font-mono opacity-70">{o.new_provider_id.slice(0, 16)}</code>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="eb-card overflow-x-auto" data-testid="notifications-replay-table">
          {loading ? (
            <div className="p-10 text-center text-navy-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-navy-500">
              <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-warning-500" />
              No mocked notifications match the current filter — try a different channel or clear the org id.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-navy-50 dark:bg-navy-700/40">
                <tr className="text-left text-navy-600 dark:text-navy-200">
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2">Channel</th>
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Subject / Body</th>
                  <th className="px-3 py-2">Org id</th>
                  <th className="px-3 py-2 whitespace-nowrap">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const meta = CHANNEL_META[it.channel] || { label: it.channel, Icon: AlertTriangle };
                  const Icon = meta.Icon;
                  const isSel = selected.has(it.id);
                  return (
                    <tr key={it.id}
                        onClick={() => toggle(it.id)}
                        className={`border-t border-navy-100 cursor-pointer hover:bg-orange-50 ${
                          isSel ? "bg-orange-50" : ""
                        }`}
                        data-testid={`row-${it.id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggle(it.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4"
                          data-testid={`cb-${it.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-navy-100 text-navy-700">
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate font-mono text-xs"
                          title={it.recipient}>
                        {it.recipient || "—"}
                      </td>
                      <td className="px-3 py-2 max-w-[400px] truncate text-navy-700"
                          title={it.subject || it.body}>
                        {it.subject || it.body || "(no subject)"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-navy-500">
                        {it.org_id || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-navy-500 whitespace-nowrap">
                        {it.created_at?.replace("T", " ").slice(0, 19) || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
